/**
 * printBlankForm — iter-293 (Feb 2026)
 * =====================================
 * Build and open a print-ready A4 document for the Company Blank Form.
 * The caller supplies an array of HTML strings (one per page body).
 *
 * The header + footer use the SAME brand config as Receipt Studio and
 * printQuotation (/api/admin/receipt-studio/brand), ensuring the whole
 * platform renders the same CloudGM identity.
 *
 * No data is persisted anywhere — this is a pure in-browser utility.
 */

const FACTORY_BRAND = {
  logo_url:          '/pdf-logo-cloudgm.png',
  accent_from:       '#3B9EDC',
  accent_to:         '#F5A55A',
  brand_name_ar:     'CLOUDGM FZ LLC',
  brand_name_en:     'CLOUDGM FZ LLC',
  footer_sites:      ['cloudgm.com', 'cloudgm.business'],
  footer_phone:      '+971 50 151 1917',
  footer_emails:     ['info@cloudgm.com', 'info@cloudgm.business'],
  footer_tagline_ar: 'شكراً لتعاملكم معنا',
  footer_tagline_en: 'Thank you for your business',
};

const BRAND_CACHE_TTL_MS = 5 * 60 * 1000;
let _brandCache = null;
let _brandCacheAt = 0;

export const fetchBlankFormBrand = async () => {
  const now = Date.now();
  if (_brandCache && (now - _brandCacheAt) < BRAND_CACHE_TTL_MS) return _brandCache;
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
    const r = await fetch(`${base}/api/admin/receipt-studio/brand`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!r.ok) throw new Error(`brand fetch ${r.status}`);
    const data = await r.json();
    _brandCache = { ...FACTORY_BRAND, ...(data?.data || {}) };
    _brandCacheAt = now;
  } catch {
    _brandCache = FACTORY_BRAND;
    _brandCacheAt = now;
  }
  return _brandCache;
};

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

const buildHeader = (brand, isAr) => {
  const NAME = isAr ? (brand.brand_name_ar || brand.brand_name_en) : (brand.brand_name_en || brand.brand_name_ar);
  const LOGO = brand.logo_url || FACTORY_BRAND.logo_url;
  const todayStr = new Date().toLocaleDateString(isAr ? 'ar-AE' : 'en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
  return `
    <div class="brand-strip"></div>
    <div class="bf-header">
      <img class="bf-logo" src="${esc(LOGO)}" alt="" onerror="this.style.display='none'">
      <div class="bf-brand">
        <h1>${esc(NAME || '')}</h1>
      </div>
      <div class="bf-meta">
        <div class="bf-date">${esc(todayStr)}</div>
      </div>
    </div>`;
};

const buildFooter = (brand, isAr) => {
  const TAG = isAr ? (brand.footer_tagline_ar || '') : (brand.footer_tagline_en || '');
  const NAME = isAr ? (brand.brand_name_ar || '') : (brand.brand_name_en || '');
  const SITES  = (brand.footer_sites  || []).join(' · ');
  const PHONE  = brand.footer_phone || '';
  const EMAILS = (brand.footer_emails || []).join(' · ');
  return `
    <div class="bf-footer">
      <div class="bar"></div>
      ${TAG ? `<div class="tag">${esc(TAG)}</div>` : ''}
      <div class="row">🌐 ${esc(SITES)}</div>
      <div class="row">📞 ${esc(PHONE)}</div>
      <div class="row">✉ ${esc(EMAILS)}</div>
      <div class="row" style="margin-top:6px;">${esc(NAME)}</div>
    </div>`;
};

const BASE_CSS = (FROM, TO, isDir) => `
  :root {
    --from: ${FROM}; --to: ${TO};
    --ink-900: #0f172a; --ink-600: #334155; --ink-400: #64748b; --ink-200: #e2e8f0;
    --paper: #ffffff; --tint: #f8fafc;
    --brand-grad: linear-gradient(90deg, var(--from) 0%, var(--to) 100%);
  }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  html, body { margin: 0; padding: 0; background: var(--tint); color: var(--ink-900); font-size: 12px; line-height: 1.6; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
  @page { size: A4; margin: 10mm; }

  .brand-strip { height: 6px; background: var(--brand-grad); margin: 0 0 20px 0; border-radius: 2px; }
  .bf-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; margin-bottom: 18px; }
  .bf-logo { height: 56px; width: auto; }
  .bf-brand h1 { margin: 0; font-size: 15px; font-weight: 700; color: var(--ink-900); }
  .bf-meta { text-align: ${isDir === 'rtl' ? 'left' : 'right'}; }
  .bf-date { font-size: 10px; color: var(--ink-400); }

  .bf-body { min-height: 220mm; font-size: 13px; line-height: 1.9; color: var(--ink-900); white-space: pre-wrap; word-break: break-word; position: relative; }
  .bf-body .bf-drag-img { user-select: none; }
  .bf-body .bf-drag-img img { max-width: 100%; height: auto; }
  .bf-body h1 { font-size: 20px; font-weight: 700; color: var(--from); margin: 14px 0 10px; }
  .bf-body h2 { font-size: 16px; font-weight: 700; color: var(--ink-900); margin: 12px 0 8px; }
  .bf-body h3 { font-size: 14px; font-weight: 700; color: var(--ink-600); margin: 10px 0 6px; }
  .bf-body ul, .bf-body ol { padding-${isDir === 'rtl' ? 'right' : 'left'}: 22px; margin: 8px 0; }
  .bf-body li { margin: 4px 0; }
  .bf-body p { margin: 6px 0; }
  .bf-body strong { color: var(--ink-900); }
  .bf-body em { font-style: italic; }

  .bf-footer { margin-top: 22px; padding-top: 12px; border-top: 1px solid var(--ink-200); text-align: center; color: var(--ink-400); font-size: 9px; }
  .bf-footer .bar { height: 2px; background: var(--brand-grad); margin-bottom: 10px; border-radius: 999px; }
  .bf-footer .tag { font-weight: 700; color: var(--from); margin-bottom: 4px; font-size: 11px; }
  .bf-footer .row { margin: 2px 0; direction: ltr; }

  @media print { body { background: var(--paper); } .no-print { display: none !important; }
    *, *::before, *::after { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
  }

  .toolbar { position: fixed; top: 12px; ${isDir === 'rtl' ? 'left' : 'right'}: 12px; display: flex; gap: 8px; z-index: 100; }
  .toolbar button { padding: 8px 16px; border: none; border-radius: 6px; background: var(--brand-grad); color: #fff; font-weight: 700; cursor: pointer; font-size: 12px; }
  .toolbar button.secondary { background: #fff; color: var(--ink-900); border: 1px solid var(--ink-200); }
`;

const buildHtml = (pages, brand, isAr, autoPrint) => {
  const FROM = brand.accent_from || FACTORY_BRAND.accent_from;
  const TO   = brand.accent_to   || FACTORY_BRAND.accent_to;
  const dir  = isAr ? 'rtl' : 'ltr';
  const font = isAr ? "'Cairo'" : "'Inter'";

  const sections = pages.map((bodyHtml) => `
    <section class="bf-page" dir="${dir}" lang="${isAr ? 'ar' : 'en'}" style="font-family: ${font}, sans-serif;">
      <table class="bf-layout">
        <tfoot><tr><td>${buildFooter(brand, isAr)}</td></tr></tfoot>
        <tbody><tr><td>
          ${buildHeader(brand, isAr)}
          <div class="bf-body">${bodyHtml || ''}</div>
        </td></tr></tbody>
      </table>
    </section>
  `).join('\n');

  const printLabel = isAr ? '🖨️ طباعة' : '🖨️ Print';
  const closeLabel = isAr ? 'إغلاق' : 'Close';

  return `<!doctype html>
<html lang="${isAr ? 'ar' : 'en'}" dir="${dir}">
<head>
<meta charset="utf-8">
<title>${isAr ? 'فورم الشركة' : 'Company Form'}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&family=Inter:wght@400;600;700&display=swap">
<style>
  ${BASE_CSS(FROM, TO, dir)}
  .bf-page { max-width: 820px; margin: 0 auto 20px; padding: 32px; background: var(--paper); page-break-after: always; box-shadow: 0 4px 24px rgba(0,0,0,0.08); }
  .bf-page:last-child { page-break-after: auto; }
  /* iter-297 — table-based layout so <tfoot> (brand footer) repeats on every physical print page */
  .bf-layout { width: 100%; border-collapse: collapse; }
  .bf-layout > tbody > tr > td,
  .bf-layout > tfoot > tr > td { padding: 0; }
  .bf-layout > tfoot { display: table-footer-group; }
</style>
</head>
<body>
  <div class="toolbar no-print">
    <button onclick="window.print()">${esc(printLabel)}</button>
    <button class="secondary" onclick="window.close()">${esc(closeLabel)}</button>
  </div>
  ${sections}
  ${autoPrint ? '<script>window.addEventListener("load", function(){ setTimeout(function(){ window.print(); }, 400); });</script>' : ''}
</body>
</html>`;
};

/**
 * Open the blank form in a new window.
 * @param {string[]} pages - array of HTML strings (one per A4 page body).
 * @param {object} opts - { language: "ar"|"en", download: boolean }
 */
export const printBlankForm = async (pages, opts = {}) => {
  const safePages = Array.isArray(pages) && pages.length ? pages : [''];
  const brand = await fetchBlankFormBrand();
  const isAr = (opts.language || 'ar').toLowerCase().startsWith('ar');
  const html = buildHtml(safePages, brand, isAr, !!opts.download);

  const win = window.open('', '_blank', 'width=900,height=1100');
  if (!win) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
};
